import { defineStore } from 'pinia'
import type { Profile, UserRole } from '~/types/auth'
import type { ThemePreset } from '~/stores/settings'
import type { ClinicKind } from '~/types/collections'
import { compressImage } from '~/utils/compressImage'
import { translateSupabaseError } from '~/utils/supabase-error'
import { useClinicModulesStore } from '~/stores/clinic_modules'

interface AuthState {
  profile:               Profile | null
  clinicHasWebIntake:    boolean
  clinicHasActiveLab:    boolean
  clinicKind:            ClinicKind | null
  clinicSuspendedAt:     string | null
  loading:               boolean
  error:                 string | null
  supervisingDoctorIds:  string[]
  receptionistDoctorIds: string[]
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    profile:               null,
    clinicHasWebIntake:    false,
    clinicHasActiveLab:    false,
    clinicKind:            null,
    clinicSuspendedAt:     null,
    loading:               false,
    error:                 null,
    supervisingDoctorIds:  [],
    receptionistDoctorIds: [],
  }),

  getters: {
    isAdmin:        (s) => s.profile?.role === 'admin'
                          || (s.profile?.additional_roles ?? []).includes('admin'),
    isDoctor:       (s) => s.profile?.role === 'doctor'
                          || (s.profile?.additional_roles ?? []).includes('doctor'),
    isNurse:        (s) => s.profile?.role === 'nurse'
                          || (s.profile?.additional_roles ?? []).includes('nurse'),
    isAdministrative: (s) => s.profile?.role === 'administrative'
                          || (s.profile?.additional_roles ?? []).includes('administrative'),
    isReceptionist: (s) => s.profile?.role === 'receptionist'
                          || (s.profile?.additional_roles ?? []).includes('receptionist'),
    isAssistantDoctor: (s) => s.profile?.role === 'assistant_doctor'
                          || (s.profile?.additional_roles ?? []).includes('assistant_doctor'),
    isClinicDoctor:    (s) => s.profile?.role === 'doctor'
                          || s.profile?.role === 'assistant_doctor'
                          || (s.profile?.additional_roles ?? []).includes('doctor')
                          || (s.profile?.additional_roles ?? []).includes('assistant_doctor'),
    effectiveDoctorIds: (s): string[] => {
      if (!s.profile?.id) return []
      const isAsst = s.profile.role === 'assistant_doctor'
                   || (s.profile.additional_roles ?? []).includes('assistant_doctor')
      return isAsst ? [s.profile.id, ...s.supervisingDoctorIds] : [s.profile.id]
    },
    fullName:       (s) => s.profile?.full_name ?? '',
    role:           (s) => s.profile?.role ?? 'doctor',
    roles:          (s): UserRole[] => {
      if (!s.profile?.role) return []
      return Array.from(new Set([s.profile.role, ...(s.profile.additional_roles ?? [])]))
    },
    hasRole:        (s) => (r: UserRole) =>
      s.profile?.role === r || (s.profile?.additional_roles ?? []).includes(r),
    clinicId:       (s) => s.profile?.clinic_id ?? null,
    hasWebIntake:   (s) => s.clinicHasWebIntake,
    /** Tenant kind (mig 1230): an agency has no clinical modules and lands on /cobranza (M-AG-002). */
    isAgencyTenant: (s) => s.clinicKind === 'collections_agency',
    isAgencySuspended: (s) => s.clinicKind === 'collections_agency' && !!s.clinicSuspendedAt,
    // Módulo lab_marketplace activo en la clínica actual (mig 989, Phase 3 Task 4).
    // Leído del store de módulos ya cargado en fetchProfile — no dispara query propia.
    clinicHasMarketplace: () => useClinicModulesStore().hasModule('lab_marketplace'),
    // /solicitudes visible si la clínica tiene intake web, recibe referidos de un lab
    // asignado (legacy), O tiene el marketplace activo (recibe referidos vía selección).
    canSeeSolicitudes: (s) => s.clinicHasWebIntake || s.clinicHasActiveLab || useClinicModulesStore().hasModule('lab_marketplace'),
    isSuperadmin:   (s) => s.profile?.is_superadmin === true && s.profile?.is_active === true,
    isScopedReceptionist: (s) => s.profile?.role === 'receptionist' && s.receptionistDoctorIds.length > 0,
    /** Permiso delegable de plataforma (mig 1114): un gestor contratado puede
     *  resolver disputas de laboratorio sin ser super-admin total. Super-admin
     *  siempre lo cumple (espejo de has_admin_scope en el servidor). */
    hasAdminScope: (s) => (scope: string) =>
      s.profile?.is_superadmin === true || (s.profile?.admin_scopes ?? []).includes(scope),
  },

  actions: {
    async fetchProfile() {
      const supabase = useSupabaseClient()
      const user     = useSupabaseUser()
      if (!user.value) return

      this.loading = true
      this.error   = null
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.value.id)
          .single()

        if (error) throw error
        this.profile = data as Profile

        if (!this.profile?.is_active) {
          this.clearProfile()
          throw new Error('account_inactive')
        }

        if (this.profile?.role === 'assistant_doctor') {
          const { data: assignments } = await supabase
            .from('doctor_assistant_assignments')
            .select('supervising_doctor_id')
            .eq('assistant_id', user.value!.id)
          this.supervisingDoctorIds = (assignments ?? []).map((r: any) => r.supervising_doctor_id)
        } else {
          this.supervisingDoctorIds = []
        }

        if (this.profile?.role === 'receptionist') {
          try {
            const { data: recepAssignments } = await supabase
              .from('user_doctor_assignments')
              .select('doctor_id')
              .eq('user_id', user.value!.id)
            this.receptionistDoctorIds = (recepAssignments ?? []).map((r: any) => r.doctor_id)
          } catch {
            this.receptionistDoctorIds = []
          }
        } else {
          this.receptionistDoctorIds = []
        }

        // Cuando un superadmin suplanta una clínica, el JWT app_metadata
        // tiene impersonated_clinic_id. RLS lo respeta vía auth_clinic_id(),
        // pero el store de módulos/clinic necesita ese id explícitamente.
        const impersonatedClinicId = (user.value as any)?.app_metadata?.impersonated_clinic_id ?? null
        const activeClinicId = impersonatedClinicId ?? this.profile?.clinic_id ?? null

        if (activeClinicId) {
          const modulesStore = useClinicModulesStore()
          await modulesStore.loadForCurrentClinic(activeClinicId)
          modulesStore.subscribe(activeClinicId)

          // Intake web (flag por clínica) + asignación activa a un lab (recibe referidos).
          // Cualquiera de los dos habilita /solicitudes (ver getter canSeeSolicitudes).
          const [{ data: clinic }, { data: labAssignment }, kindRes] = await Promise.all([
            supabase
              .from('clinics')
              .select('has_web_intake')
              .eq('id', activeClinicId)
              .single(),
            supabase
              .from('clinic_lab_assignment')
              // mig 989 (Phase 3 Task 4): clinic_lab_assignment is no longer
              // UNIQUE(clinic_id) — a clinic can carry more than one active
              // zone-commission pair. Only existence matters here, so .limit(1)
              // keeps .maybeSingle() safe regardless of pair count.
              .select('id')
              .eq('clinic_id', activeClinicId)
              .eq('is_active', true)
              .limit(1)
              .maybeSingle(),
            // clinics.kind (mig 1230) in its OWN query: if the FE ships before the
            // migration, a combined select errors and has_web_intake would silently read
            // false for every clinic. Here any failure only degrades kind to 'clinic'.
            Promise.resolve(
              supabase
                .from('clinics')
                .select('kind, suspended_at')
                .eq('id', activeClinicId)
                .maybeSingle(),
            ).catch(() => ({ data: null, error: { message: 'clinics.kind lookup failed' } })),
          ])
          this.clinicHasWebIntake = clinic?.has_web_intake ?? false
          this.clinicHasActiveLab = !!labAssignment
          // 42703 = column doesn't exist yet (mig 1230 not applied) — expected during
          // rollout, degrade silently. Any other error (RLS, network, etc.) still
          // degrades to 'clinic' (ruling P2-R15) but is unexpected, so log it.
          if (kindRes.error && (kindRes.error as any).code !== '42703') {
            console.warn('[auth] clinics.kind lookup failed:', kindRes.error)
          }
          this.clinicKind = kindRes.error
            ? 'clinic'
            : (((kindRes.data as { kind?: ClinicKind } | null)?.kind) ?? 'clinic')
          this.clinicSuspendedAt = kindRes.error
            ? null
            : (((kindRes.data as { suspended_at?: string | null } | null)?.suspended_at) ?? null)
        } else {
          this.clinicHasWebIntake = false
          this.clinicHasActiveLab = false
          this.clinicKind = null
          this.clinicSuspendedAt = null
        }
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally {
        this.loading = false
      }
    },

    clearProfile() {
      const modulesStore = useClinicModulesStore()
      modulesStore.unsubscribe()
      this.profile               = null
      this.clinicHasWebIntake    = false
      this.clinicHasActiveLab    = false
      this.clinicKind            = null
      this.clinicSuspendedAt     = null
      this.error                 = null
      this.supervisingDoctorIds  = []
      this.receptionistDoctorIds = []
    },

    async updateTheme(preset: ThemePreset) {
      if (!this.profile) return
      const supabase = useSupabaseClient()
      const prev = this.profile.theme_preset
      this.profile.theme_preset = preset
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ theme_preset: preset })
          .eq('id', this.profile.id)
          .select('id')
          .single()
        if (error) throw error
      } catch (e: any) {
        this.profile.theme_preset = prev
        throw e
      }
    },

    async uploadProfileImage(file: File): Promise<void> {
      const supabase = useSupabaseClient()
      const user     = useSupabaseUser()
      if (!user.value || !this.profile) throw new Error('No autenticado')
      this.loading = true
      try {
        // Validar MIME y tamaño (50 MB input; compresión client-side → ≤10 MB final)
        const ALLOWED = ['image/jpeg', 'image/png', 'image/webp']
        if (!ALLOWED.includes(file.type)) {
          throw new Error('Solo se permiten imágenes JPG, PNG o WebP')
        }
        if (file.size > 50 * 1024 * 1024) {
          throw new Error('La imagen es demasiado grande (máx. 50 MB)')
        }

        const compressed = await compressImage(file, { maxDimension: 1024, quality: 0.85 })
        if (compressed.size > 10 * 1024 * 1024) {
          throw new Error('No se pudo comprimir la foto lo suficiente. Intenta con otra imagen.')
        }

        // Avatar en R2 (bucket público assets) vía presign — Supabase Storage
        // retirado 2026-07. El server construye el path profile-images/<uid>/…
        const { data: up, error: upErr } = await supabase.functions.invoke('r2-upload', {
          body: { bucket: 'assets', asset_kind: 'profile-image', file_name: 'avatar.jpg', file_type: 'image/jpeg', file_size: compressed.size },
        })
        if (upErr || up?.error || !up?.presigned_url) {
          throw new Error(up?.error ?? upErr?.message ?? 'No se pudo subir la foto')
        }
        const put = await fetch(up.presigned_url, {
          method: 'PUT', body: compressed, headers: { 'Content-Type': 'image/jpeg' },
        })
        if (!put.ok) throw new Error(`Upload falló: ${put.status}`)
        // cache-buster: el path es fijo (avatar.jpg), la URL debe variar
        const url = `${up.public_url}?v=${Date.now()}`

        const { error: dbError } = await supabase
          .from('profiles')
          .update({ profile_image: url })
          .eq('id', this.profile.id)
        if (dbError) throw dbError

        this.profile.profile_image = url
      } catch (e: any) {
        console.error(e)
        this.error = 'Error al subir la foto de perfil'
        throw e
      } finally {
        this.loading = false
      }
    },

    async updateProfile(data: {
      full_name?:          string
      phone?:              string
      phone_country_code?: string
      specialty?:          string
      license_number?:     string
      doctor_title?:       string | null
      rooms?:              string[]
    }): Promise<void> {
      const supabase = useSupabaseClient()
      if (!this.profile) throw new Error('No autenticado')

      const { error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', this.profile.id)
      if (error) throw error

      Object.assign(this.profile, data)
    },

    async removeProfileImage(): Promise<void> {
      const supabase = useSupabaseClient()
      if (!this.profile) throw new Error('No autenticado')

      const imagePath = this.profile.profile_image
      if (imagePath) {
        // Key CON el prefijo profile-images/ (keys R2) y tolerante al ?v= cache-buster
        const match = imagePath.match(/\/(profile-images\/[^?]+)/)
        if (match?.[1]) {
          const { data: del, error: deleteErr } = await supabase.functions.invoke('r2-delete', {
            body: { bucket: 'assets', storage_path: match[1] },
          })
          if (deleteErr || del?.error) {
            console.warn('[auth] Failed to remove old profile image from storage:', del?.error ?? deleteErr?.message)
          }
        }
      }

      const { error } = await supabase
        .from('profiles')
        .update({ profile_image: null })
        .eq('id', this.profile.id)
      if (error) throw error

      this.profile.profile_image = null
    },
  },
})
